'use client';

/**
 * WithdrawWizard - Withdraw Funds Flow (Sheet)
 * 
 * Two-step wizard for withdrawing funds from SupaWallet:
 * 1. Choose destination (Bank Account or Crypto Wallet)
 * 2. Select account/wallet and enter amount
 * 
 * Uses same Sheet pattern as P2PTransferSheet:
 * - Mobile (<1024px): Bottom sheet, h=95vh
 * - Desktop (≥1024px): Right sheet, w=28rem
 * 
 * @see PRD-WALLET-HUB
 */

import { NetworkIcon } from '@/components/NetworkIcons';
import { Alert, AlertDescription, AlertTitle } from '@/components/ui/alert';
import { Button } from '@/components/ui/button';
import { Card, CardContent } from '@/components/ui/card';
import { CurrencyIcon } from '@/components/ui/CurrencyIcon';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { SFIcon } from '@/components/ui/sf-icon';
import { Sheet, SheetContent, SheetDescription, SheetFooter, SheetHeader, SheetTitle } from '@/components/ui/sheet';
import { Skeleton } from '@/components/ui/skeleton';
import { Heading, Text } from '@/components/ui/typography';
import { EUR_PEGGED_STABLECOINS, USD_PEGGED_STABLECOINS, useEurToUsdRate, useUsdToEurRate } from '@/features/user/wallet/hooks/useExchangeRates';
import { getEstimatedArrival, useFeeCalculation } from '@/features/user/wallet/hooks/useFeeCalculation';
import {
    BridgeWallet,
    ExternalAccount,
    useExternalAccounts,
    useFiatWithdrawal,
    useWalletBalances,
    useWalletTransfer
} from '@/features/user/wallet/hooks/useWallet';
import { WithdrawSummaryCard } from '@/features/user/wallet/WithdrawSummaryCard';
import { useIsMobile } from '@/hooks/use-mobile';
import { toast } from '@/hooks/use-toast';
import { SavedAddress, useMarkAddressUsed, useSavedAddresses } from '@/hooks/useSavedAddresses';
import { devLog } from '@/lib/logger';
import { sfIcons } from '@/lib/sf-icons';
import { cn } from '@/lib/utils';
import { semanticTokens } from '@packages/config/theme';
import {
    ArrowUpRight,
    Loader2,
    ShieldAlert,
    Zap
} from 'lucide-react';
import { useEffect, useMemo, useState } from 'react';
import { useNavigate } from 'react-router-dom';

type WizardStep = 'choose' | 'bank' | 'crypto';

interface WithdrawWizardProps {
  open: boolean;
  onClose: () => void;
  onSuccess?: () => void;
}

export function WithdrawWizard({ open, onClose, onSuccess }: WithdrawWizardProps) {
  const isMobile = useIsMobile();
  const navigate = useNavigate();

  // Wizard state
  const [step, setStep] = useState<WizardStep>('choose');
  const [selectedAccount, setSelectedAccount] = useState<ExternalAccount | null>(null);
  const [selectedWallet, setSelectedWallet] = useState<BridgeWallet | null>(null);
  const [selectedSavedAddress, setSelectedSavedAddress] = useState<SavedAddress | null>(null);
  const [amount, setAmount] = useState('');
  const [currency, setCurrency] = useState<'USD' | 'EUR'>('USD');
  const [cryptoCurrency, setCryptoCurrency] = useState<string>(''); // Selected token for crypto withdrawal
  const [paymentRail, setPaymentRail] = useState<'ach' | 'wire' | 'sepa'>('ach');
  const [destinationAddress, setDestinationAddress] = useState('');
  const [destinationChain, setDestinationChain] = useState('ethereum');
  const [showReview, setShowReview] = useState(false); // Quote preview step

  // Data
  const { data: externalAccounts, isLoading: isLoadingAccounts } = useExternalAccounts({ enabled: open });
  const { data: wallets, isLoading: isLoadingWallets, refetch: refetchWallets } = useWalletBalances();
  const { data: savedAddresses, isLoading: isLoadingSavedAddresses } = useSavedAddresses();

  // Force refetch wallets when modal opens to ensure fresh data
  useEffect(() => {
    if (open && !isLoadingWallets) {
      devLog('[WithdrawWizard] Modal opened, refetching wallets...');
      refetchWallets().catch(err => {
        window.console?.error?.('[WithdrawWizard] Failed to refetch wallets:', err);
      });
    }
  }, [open, refetchWallets, isLoadingWallets]);
  const markAddressUsed = useMarkAddressUsed();

  // Exchange rates for cross-currency conversions
  const {
    data: usdToEurRate = 0.93,
    isLoading: isLoadingUsdEur,
    dataUpdatedAt: usdEurUpdatedAt
  } = useUsdToEurRate();

  const {
    data: eurToUsdRate = 1.08,
    isLoading: isLoadingEurUsd,
    dataUpdatedAt: eurUsdUpdatedAt
  } = useEurToUsdRate();

  // Detect all available stablecoins with balances
  const availableStablecoins = useMemo(() => {
    // Aggressive logging for debugging
    devLog('[WithdrawWizard] Calculating availableStablecoins:', {
      wallets: wallets,
      walletsType: typeof wallets,
      walletsIsArray: Array.isArray(wallets),
      walletsLength: Array.isArray(wallets) ? wallets.length : 'N/A',
      timestamp: new Date().toISOString()
    });

    if (!wallets || !Array.isArray(wallets)) {
      devLog('[WithdrawWizard] No wallets or not an array');
      return [];
    }

    const balances = new Map<string, number>();
    let totalProcessed = 0;
    let totalSkipped = 0;

    wallets.forEach((wallet, walletIndex) => {
      devLog(`[WithdrawWizard] Processing wallet ${walletIndex}:`, {
        id: wallet.id,
        chain: wallet.chain,
        hasBalances: !!wallet.balances,
        balancesType: typeof wallet.balances,
        balancesIsArray: Array.isArray(wallet.balances),
        balancesValue: wallet.balances
      });

      if (!wallet?.balances || !Array.isArray(wallet.balances)) {
        totalSkipped++;
        devLog(`[WithdrawWizard] Wallet ${walletIndex} has no balances or balances is not array`);
        return;
      }
      
      wallet.balances.forEach((b, balanceIndex) => {
        // Normalize currency - handle both string and undefined
        const currencyRaw = b.currency;
        const curr = currencyRaw ? String(currencyRaw).toUpperCase().trim() : null;
        const balanceStr = b.balance != null ? String(b.balance) : '0';
        const balanceValue = parseFloat(balanceStr) || 0;
        
        devLog(`[WithdrawWizard] Processing balance ${balanceIndex}:`, {
          currencyRaw: currencyRaw,
          currency: b.currency,
          currencyUpper: curr,
          balance: b.balance,
          balanceStr: balanceStr,
          balanceParsed: balanceValue,
          isStablecoin: curr && ['USDC', 'USDT', 'EURC', 'DAI'].includes(curr),
          isValidBalance: !isNaN(balanceValue) && balanceValue > 0
        });
        
        // Only consider stablecoins (USDC, USDT, EURC, DAI) with positive balance
        if (curr && ['USDC', 'USDT', 'EURC', 'DAI'].includes(curr) && balanceValue > 0) {
          const current = balances.get(curr) || 0;
          const newBalance = current + balanceValue;
          balances.set(curr, newBalance);
          totalProcessed++;
          devLog(`[WithdrawWizard] Updated ${curr} balance: ${current} + ${balanceValue} = ${newBalance}`);
        } else {
          totalSkipped++;
          if (balanceValue > 0) {
            devLog(`[WithdrawWizard] Skipped balance ${balanceIndex}: currency=${curr}, not a stablecoin or zero balance`);
          }
        }
      });
    });

    const result = Array.from(balances.entries())
      .map(([currency, balance]) => ({ currency, balance }))
      .filter(item => item.balance > 0)
      .sort((a, b) => b.balance - a.balance); // Sort by balance descending

    devLog(`[WithdrawWizard] Final availableStablecoins:`, {
      result,
      totalProcessed,
      totalSkipped,
      walletsCount: wallets.length
    });

    return result;
  }, [wallets]);

  // Source stablecoin selection (flexible, not locked to target currency)
  const [sourceStablecoin, setSourceStablecoin] = useState<string>('');

  // Auto-select largest balance stablecoin when available coins change
  useEffect(() => {
    devLog('[WithdrawWizard] Auto-select effect:', {
      availableStablecoinsLength: availableStablecoins.length,
      currentSourceStablecoin: sourceStablecoin,
      availableStablecoins: availableStablecoins
    });

    if (availableStablecoins.length > 0 && !sourceStablecoin) {
      const selected = availableStablecoins[0].currency;
      devLog('[WithdrawWizard] Auto-selecting stablecoin:', selected);
      setSourceStablecoin(selected);
    } else if (availableStablecoins.length === 0 && sourceStablecoin) {
      // Reset if no stablecoins available
      devLog('[WithdrawWizard] No stablecoins available, resetting sourceStablecoin');
      setSourceStablecoin('');
    }
  }, [availableStablecoins, sourceStablecoin]);

  // Available balance in selected source stablecoin (must be before useEffect that logs it)
  const availableBalance = useMemo(() => {
    devLog('[WithdrawWizard] Calculating availableBalance:', {
      wallets: wallets,
      walletsLength: Array.isArray(wallets) ? wallets.length : 'N/A',
      sourceStablecoin,
      isLoadingWallets,
      availableStablecoinsLength: availableStablecoins.length
    });

    if (!wallets || !Array.isArray(wallets)) {
      devLog('[WithdrawWizard] Cannot calculate balance: wallets not array');
      return 0;
    }

    if (!sourceStablecoin) {
      devLog('[WithdrawWizard] Cannot calculate balance: no sourceStablecoin');
      return 0;
    }

    // Sum all balances in the selected source stablecoin
    const result = wallets.reduce((sum, wallet, walletIndex) => {
      if (!wallet?.balances || !Array.isArray(wallet.balances)) {
        devLog(`[WithdrawWizard] Wallet ${walletIndex} has no balances`);
        return sum;
      }
      
      // Normalize currency comparison
      const balance = wallet.balances.find(b => {
        const bCurrency = b.currency ? String(b.currency).toUpperCase().trim() : '';
        const targetCurrency = sourceStablecoin.toUpperCase().trim();
        return bCurrency === targetCurrency;
      });
      
      if (balance) {
        const balanceStr = balance.balance != null ? String(balance.balance) : '0';
        const amount = parseFloat(balanceStr) || 0;
        devLog(`[WithdrawWizard] Found ${sourceStablecoin} balance in wallet ${walletIndex}:`, {
          currency: balance.currency,
          balance: balance.balance,
          balanceStr: balanceStr,
          amount: amount
        });
        return sum + amount;
      }
      return sum;
    }, 0);

    devLog('[WithdrawWizard] Final availableBalance:', result);
    return result;
  }, [wallets, sourceStablecoin, isLoadingWallets, availableStablecoins.length]);

  // Debug: Log wallet data when modal opens
  useEffect(() => {
    if (open) {
      devLog('[WithdrawWizard] ===== MODAL OPENED =====');
      devLog('[WithdrawWizard] Wallet data:', {
        walletsCount: wallets?.length || 0,
        wallets: wallets,
        isLoading: isLoadingWallets,
        availableStablecoins: availableStablecoins,
        sourceStablecoin,
        availableBalance,
        timestamp: new Date().toISOString()
      });
      if (wallets && Array.isArray(wallets) && wallets.length > 0) {
        wallets.forEach((wallet, idx) => {
          devLog(`[WithdrawWizard] Wallet ${idx}:`, {
            id: wallet.id,
            chain: wallet.chain,
            balancesCount: wallet.balances?.length || 0,
            balances: wallet.balances
          });
        });
      }
    }
  }, [open, wallets, isLoadingWallets, availableStablecoins, sourceStablecoin, availableBalance]);

  // Mutations
  const fiatWithdrawal = useFiatWithdrawal();
  const cryptoTransfer = useWalletTransfer();

  // Fee calculation for review screen
  const fees = useFeeCalculation({
    amount: parseFloat(amount) || 0,
    currency,
    destinationType: step === 'bank' ? 'bank' : 'crypto',
    paymentRail: step === 'bank' ? paymentRail : undefined,
    network: step === 'crypto' ? destinationChain : undefined,
    exchangeRate: 1,
  });

  // Reset state when sheet closes
  useEffect(() => {
    if (!open) {
      setStep('choose');
      setSelectedAccount(null);
      setSelectedWallet(null);
      setSelectedSavedAddress(null);
      setAmount('');
      setShowReview(false); // Reset review state
      setCurrency('USD');
      setSourceStablecoin(''); // Reset source stablecoin
      setCryptoCurrency('');
      setPaymentRail('ach');
      setDestinationAddress('');
      setDestinationChain('ethereum');
    }
  }, [open]);

  // Validation
  const isValid = useMemo(() => {
    const amt = parseFloat(amount);
    if (!amt || amt <= 0) return false;

    if (step === 'bank') {
      return !!selectedAccount;
    }
    if (step === 'crypto') {
      // Must have: source wallet, selected token, destination address
      if (!selectedWallet) return false;
      if (!cryptoCurrency) return false; // Must select token
      if (!destinationAddress || destinationAddress.length < 10) return false;

      // Check sufficient balance for selected token
      const tokenBalance = selectedWallet.balances.find(b =>
        b.currency.toUpperCase() === cryptoCurrency.toUpperCase()
      );
      if (!tokenBalance || parseFloat(tokenBalance.balance) < amt) return false;

      // Basic address validation by chain
      const chain = selectedWallet.chain?.toLowerCase() || 'ethereum';
      if (chain === 'solana') {
        // Solana: base58 format, typically 32-44 chars
        return /^[1-9A-HJ-NP-Za-km-z]{32,44}$/.test(destinationAddress);
      } else {
        // EVM chains (ethereum, base, polygon, etc.): 0x prefix + 40 hex chars
        return /^0x[a-fA-F0-9]{40}$/.test(destinationAddress);
      }
    }
    return false;
  }, [amount, step, selectedAccount, selectedWallet, cryptoCurrency, destinationAddress]);

  // Can proceed to review (for bank withdrawals)
  const canProceed = useMemo(() => {
    if (step !== 'bank' || !selectedAccount || !amount) return false;
    const amt = parseFloat(amount) || 0;
    if (amt <= 0) return false;

    // Check sufficient balance in source stablecoin
    return availableBalance >= amt;
  }, [step, selectedAccount, amount, availableBalance]);

  // Cross-currency conversion info
  const conversionInfo = useMemo(() => {
    if (!sourceStablecoin || !currency) return null;

    const isSourceUsd = USD_PEGGED_STABLECOINS.includes(sourceStablecoin as any);
    const isSourceEur = EUR_PEGGED_STABLECOINS.includes(sourceStablecoin as any);
    const isTargetUsd = currency === 'USD';
    const isTargetEur = currency === 'EUR';

    // Determine if conversion is needed
    const needsConversion =
      (isSourceUsd && isTargetEur) ||
      (isSourceEur && isTargetUsd);

    if (!needsConversion) return null;

    // Get the appropriate rate
    const rate = isSourceUsd && isTargetEur ? usdToEurRate : eurToUsdRate;
    const isLoadingRate = isLoadingUsdEur || isLoadingEurUsd;

    // Calculate source amount needed for target amount
    const targetAmount = parseFloat(amount) || 0;
    const sourceAmount = targetAmount > 0 ? targetAmount / rate : 0;

    // Get most recent rate update timestamp
    const rateUpdatedAt = Math.max(usdEurUpdatedAt || 0, eurUsdUpdatedAt || 0);
    const rateAge = rateUpdatedAt > 0 ? Date.now() - rateUpdatedAt : null;
    const isRateStale = rateAge !== null && rateAge > 60000; // Older than 1 minute

    return {
      needsConversion: true,
      rate,
      isLoadingRate,
      sourceAmount,
      targetAmount,
      rateUpdatedAt: rateUpdatedAt > 0 ? new Date(rateUpdatedAt) : null,
      isRateStale,
    };
  }, [sourceStablecoin, currency, amount, usdToEurRate, eurToUsdRate, isLoadingUsdEur, isLoadingEurUsd, usdEurUpdatedAt, eurUsdUpdatedAt]);

  // Handle proceeding to review (shows quote preview)
  const handleProceedToReview = () => {
    if (!selectedAccount || !amount) return;

    const amt = parseFloat(amount);
    if (isNaN(amt) || amt <= 0) {
      toast.error('Please enter a valid amount');
      return;
    }

    // Check if we have any stablecoins available
    if (availableStablecoins.length === 0) {
      toast.error(
        'No stablecoin balance available. Add USDC, USDT, or EURC to withdraw.',
        { duration: 10000 }
      );
      return;
    }

    // Find wallet with sufficient balance in selected source stablecoin
    const sourceWallet = wallets?.find(w =>
      w.balances.some(b =>
        b.currency.toUpperCase() === sourceStablecoin.toUpperCase() &&
        parseFloat(b.balance) >= amt
      )
    );

    if (!sourceWallet) {
      // Try to suggest alternatives
      const alternatives = availableStablecoins
        .filter(s => s.balance >= amt)
        .map(s => `${s.currency} ($${s.balance.toFixed(2)})`)
        .join(' or ');

      toast.error(
        `Insufficient ${sourceStablecoin} balance. ` +
        (alternatives
          ? `Available: $${availableBalance.toFixed(2)}. Try using ${alternatives}.`
          : `You need at least $${amt.toFixed(2)} ${sourceStablecoin}.`
        ),
        { duration: 10000 }
      );
      return;
    }

    // Show review screen
    setShowReview(true);
  };

  // Handle confirmed withdrawal (after review)
  const handleConfirmWithdraw = async () => {
    if (!selectedAccount || !amount) return;

    const amt = parseFloat(amount);

    // Find wallet with sufficient balance in selected source stablecoin
    const sourceWallet = wallets?.find(w =>
      w.balances.some(b =>
        b.currency.toUpperCase() === sourceStablecoin.toUpperCase() &&
        parseFloat(b.balance) >= amt
      )
    );

    // Defensive: Validate wallet and wallet ID exist
    if (!sourceWallet || (!sourceWallet.bridge_wallet_id && !sourceWallet.id)) {
      toast.error(
        `Insufficient ${sourceStablecoin} balance. ` +
        `Available: ${availableBalance.toFixed(2)} ${sourceStablecoin}`,
        { duration: 8000 }
      );
      return;
    }

    try {
      await fiatWithdrawal.mutateAsync({
        from_wallet_id: sourceWallet.bridge_wallet_id || sourceWallet.id,
        to_external_account_id: selectedAccount.id,
        amount,
        currency,
        payment_rail: paymentRail,
        source_currency: sourceStablecoin, // FLEXIBLE: Can be USDC, EURC, USDT, etc.
      });

      toast.success('Withdrawal initiated!');
      onSuccess?.();
      onClose();
    } catch (error) {
      toast.error(error instanceof Error ? error.message : 'Withdrawal failed');
    }
  };

  // Handle crypto withdrawal
  const handleCryptoWithdraw = async () => {
    if (!destinationAddress || !amount || !selectedWallet || !cryptoCurrency) return;

    // Validate sufficient balance for selected token
    const tokenBalance = selectedWallet.balances.find(b =>
      b.currency.toUpperCase() === cryptoCurrency.toUpperCase()
    );
    if (!tokenBalance || parseFloat(tokenBalance.balance) < parseFloat(amount)) {
      toast.error(`Insufficient ${cryptoCurrency.toUpperCase()} balance`);
      return;
    }

    try {
      await cryptoTransfer.mutateAsync({
        from_wallet_id: selectedWallet.bridge_wallet_id || selectedWallet.id,
        destination_address: destinationAddress,
        destination_chain: selectedWallet.chain?.toLowerCase() || destinationChain,
        amount,
        currency: cryptoCurrency.toUpperCase(),
      });

      // Mark saved address as used (for sorting)
      if (selectedSavedAddress) {
        markAddressUsed.mutate(selectedSavedAddress.id);
      }

      toast.success('Transfer initiated!');
      onSuccess?.();
      onClose();
    } catch (error) {
      toast.error(error instanceof Error ? error.message : 'Transfer failed');
    }
  };

  // Go back handler
  const handleBack = () => {
    if (step === 'bank') {
      if (selectedAccount) {
        setSelectedAccount(null);
      } else {
        setStep('choose');
      }
    } else if (step === 'crypto') {
      setStep('choose');
    }
  };

  // Navigate to add bank account
  const handleAddBankAccount = () => {
    onClose();
    navigate('/dashboard/send');
  };

  const sheetSide = isMobile ? 'bottom' : 'right';
  const showBackButton = step !== 'choose';
  const isPending = fiatWithdrawal.isPending || cryptoTransfer.isPending;

  // Get title based on step
  const getTitle = () => {
    if (step === 'choose') return 'Withdraw';
    if (step === 'bank') return selectedAccount ? 'Enter Amount' : 'Select Account';
    if (step === 'crypto') return 'Send Crypto';
    return 'Withdraw';
  };

  // Get description based on step
  const getDescription = () => {
    if (step === 'choose') return 'Where do you want to send your funds?';
    if (step === 'bank') return selectedAccount ? `Withdraw to ${selectedAccount.bank_name || 'bank account'}` : 'Choose a bank account for withdrawal';
    if (step === 'crypto') return 'Send to external wallet address';
    return '';
  };

  return (
    <Sheet open={open} onOpenChange={onClose}>
      <SheetContent
        side={sheetSide}
        className={cn(
          'flex flex-col',
          isMobile
            ? 'h-[95vh] max-h-[95vh]'
            : 'w-[28rem] max-w-[28rem]',
          semanticTokens.motion.standard
        )}
      >
        {/* Header */}
        <SheetHeader className="flex-shrink-0 pb-4 border-b">
          <div className={cn("flex items-center", semanticTokens.gap.smMd, showBackButton && "-ml-2")}>
            {showBackButton && (
              <Button
                variant="ghost"
                size="icon"
                onClick={handleBack}
                className="min-h-[44px] min-w-[44px]"
                aria-label="Go back"
              >
                <SFIcon icon={sfIcons.back} className="h-5 w-5" />
              </Button>
            )}
            <div className={cn(
              semanticTokens.spacing.smPlus,
              semanticTokens.radius.xl,
              semanticTokens.bg.muted
            )}>
              <ArrowUpRight className="h-5 w-5 text-primary" aria-hidden="true" />
            </div>
            <div>
              <SheetTitle className={semanticTokens.typography.h3}>
                {getTitle()}
              </SheetTitle>
              <SheetDescription className={cn(semanticTokens.typography.caption, 'mt-0.5')}>
                {getDescription()}
              </SheetDescription>
            </div>
          </div>
        </SheetHeader>

        {/* Content - Scrollable */}
        <div className={cn(
          "flex-1 overflow-y-auto overflow-x-hidden min-w-0",
          semanticTokens.padding.vertical.lg,
          semanticTokens.stack.md
        )}>

          {/* Step: Choose Destination */}
          {step === 'choose' && (
            <div className={cn("grid", semanticTokens.gap.md)}>
              {/* Available Balance */}
              <div className={cn(
                semanticTokens.spacing.md,
                semanticTokens.radius.xl,
                semanticTokens.bg.muted
              )}>
                <Text variant="caption" color="muted">Available to withdraw</Text>
                <Text variant="lg" className="font-semibold tabular-nums">
                  {(() => {
                    // Log render state for debugging
                    if (open) {
                      devLog('[WithdrawWizard] Rendering balance display:', {
                        isLoadingWallets,
                        availableStablecoinsLength: availableStablecoins.length,
                        sourceStablecoin,
                        availableBalance,
                        walletsCount: wallets?.length || 0
                      });
                    }

                    if (isLoadingWallets) {
                      return 'Loading...';
                    }

                    if (availableStablecoins.length === 0) {
                      // Show $0.00 if no stablecoins, but log why
                      devLog('[WithdrawWizard] No stablecoins available:', {
                        walletsCount: wallets?.length || 0,
                        wallets: wallets
                      });
                      return '$0.00';
                    }

                    if (!sourceStablecoin) {
                      return 'Select a stablecoin';
                    }

                    if (availableBalance === 0) {
                      devLog('[WithdrawWizard] Balance is 0 for', sourceStablecoin);
                    }

                    return `$${availableBalance.toLocaleString('en-US', { minimumFractionDigits: 2, maximumFractionDigits: 2 })} ${sourceStablecoin}`;
                  })()}
                </Text>
              </div>

              {/* Bank Account Option */}
              <Card
                className={cn(
                  'cursor-pointer transition-all',
                  semanticTokens.motion.standard,
                  'hover:shadow-md hover:border-primary/50',
                  'focus-visible:ring-2 focus-visible:ring-primary'
                )}
                onClick={() => setStep('bank')}
                role="button"
                tabIndex={0}
                onKeyDown={(e) => e.key === 'Enter' && setStep('bank')}
              >
                <CardContent className={semanticTokens.spacing.lg}>
                  <div className={cn("flex items-start", semanticTokens.gap.md)}>
                    <div className={cn(
                      semanticTokens.spacing.smMd,
                      semanticTokens.radius.xl,
                      'bg-blue-100 dark:bg-blue-900/30'
                    )}>
                      <SFIcon icon={sfIcons.bank} className="h-6 w-6 text-blue-600 dark:text-blue-400" />
                    </div>
                    <div className="flex-1">
                      <Heading level={4} className="mb-1">Bank Account</Heading>
                      <Text variant="sm" color="muted">
                        Withdraw to your linked bank account
                      </Text>
                      <div className={cn("flex items-center mt-3", semanticTokens.gap.sm)}>
                        <Text variant="caption" color="muted">
                          1-3 business days • ACH or Wire
                        </Text>
                      </div>
                    </div>
                  </div>
                </CardContent>
              </Card>

              {/* Crypto Wallet Option */}
              <Card
                className={cn(
                  'cursor-pointer transition-all',
                  semanticTokens.motion.standard,
                  'hover:shadow-md hover:border-primary/50',
                  'focus-visible:ring-2 focus-visible:ring-primary'
                )}
                onClick={() => setStep('crypto')}
                role="button"
                tabIndex={0}
                onKeyDown={(e) => e.key === 'Enter' && setStep('crypto')}
              >
                <CardContent className={semanticTokens.spacing.lg}>
                  <div className={cn("flex items-start", semanticTokens.gap.md)}>
                    <div className={cn(
                      semanticTokens.spacing.smMd,
                      semanticTokens.radius.xl,
                      'bg-orange-100 dark:bg-orange-900/30'
                    )}>
                      <SFIcon icon={sfIcons.wallet} className="h-6 w-6 text-orange-600 dark:text-orange-400" />
                    </div>
                    <div className="flex-1">
                      <Heading level={4} className="mb-1">Crypto Wallet</Heading>
                      <Text variant="sm" color="muted">
                        Send USDC to external wallet
                      </Text>
                      <div className={cn("flex items-center mt-3", semanticTokens.gap.sm)}>
                        <Zap className="h-4 w-4 text-green-500" />
                        <Text variant="caption" color="muted">
                          Instant • Network fees apply
                        </Text>
                      </div>
                    </div>
                  </div>
                </CardContent>
              </Card>
            </div>
          )}

          {/* Step: Bank - Select Account */}
          {step === 'bank' && !selectedAccount && (
            <div className={semanticTokens.stack.md}>
              {isLoadingAccounts ? (
                <div className={semanticTokens.stack.sm}>
                  {[1, 2].map((i) => (
                    <Skeleton key={i} className={cn("h-20 w-full", semanticTokens.radius.xl)} />
                  ))}
                </div>
              ) : externalAccounts && externalAccounts.length > 0 ? (
                <div className={cn("grid", semanticTokens.gap.smMd)}>
                  {externalAccounts.map((account) => (
                    <Card
                      key={account.id}
                      className={cn(
                        'cursor-pointer transition-all',
                        semanticTokens.motion.standard,
                        'hover:shadow-md hover:border-primary/50'
                      )}
                      onClick={() => {
                        setSelectedAccount(account);
                        // Auto-detect payment rail based on account type
                        if (account.iban && account.swift_bic) {
                          // SEPA account
                          setPaymentRail('sepa');
                          setCurrency('EUR');
                        } else if (account.routing_number && account.account_number) {
                          // ACH account (US)
                          setPaymentRail('ach');
                          setCurrency('USD');
                        }
                        // WIRE can be used for both, so we keep current selection
                      }}
                      role="button"
                      tabIndex={0}
                    >
                      <CardContent className={semanticTokens.spacing.md}>
                        <div className="flex items-center justify-between">
                          <div className={cn("flex items-center", semanticTokens.gap.smMd)}>
                            <SFIcon icon={sfIcons.bank} className="h-8 w-8 text-muted-foreground" />
                            <div>
                              <Text variant="md" className="font-medium">
                                {account.bank_name || 'Bank Account'}
                              </Text>
                              <Text variant="caption" color="muted">
                                ****{account.account_last4 || account.account_number?.slice(-4) || '****'}
                              </Text>
                            </div>
                          </div>
                          {account.status === 'active' && (
                            <SFIcon icon={sfIcons.success} className="h-5 w-5 text-green-500" />
                          )}
                        </div>
                      </CardContent>
                    </Card>
                  ))}
                </div>
              ) : (
                <div className="text-center py-8">
                  <SFIcon icon={sfIcons.bank} className="h-12 w-12 mx-auto text-muted-foreground mb-4" />
                  <Heading level={4} className="mb-2">No bank accounts linked</Heading>
                  <Text variant="sm" color="muted" className="mb-6">
                    Add a bank account to withdraw funds
                  </Text>
                </div>
              )}

              <Button
                variant={externalAccounts && externalAccounts.length > 0 ? 'outline' : 'default'}
                className={cn('w-full min-h-[52px]', semanticTokens.radius.xl)}
                onClick={handleAddBankAccount}
              >
                <SFIcon icon={sfIcons.add} className="mr-2 h-5 w-5" />
                Add Bank Account
              </Button>
            </div>
          )}

          {/* Step: Bank - Enter Amount */}
          {step === 'bank' && selectedAccount && (
            <div className={semanticTokens.stack.lg}>
              {/* Selected Account */}
              <div className={cn(
                semanticTokens.spacing.md,
                semanticTokens.radius.xl,
                semanticTokens.bg.muted
              )}>
                <div className={cn("flex items-center", semanticTokens.gap.smMd)}>
                  <SFIcon icon={sfIcons.bank} className="h-8 w-8 text-muted-foreground" />
                  <div>
                    <Text variant="md" className="font-medium">
                      {selectedAccount.bank_name || 'Bank Account'}
                    </Text>
                    <Text variant="caption" color="muted">
                      ****{selectedAccount.account_last4 || selectedAccount.account_number?.slice(-4) || '****'}
                    </Text>
                  </div>
                </div>
              </div>

              {/* Amount Input */}
              <div className={semanticTokens.stack.sm}>
                <Label className={semanticTokens.typography.label}>Amount (Paying with {sourceStablecoin || 'stablecoin'})</Label>
                <div className="relative">
                  <span className="absolute left-4 top-1/2 -translate-y-1/2 text-muted-foreground font-medium">
                    {currency === 'EUR' ? '€' : '$'}
                  </span>
                  <Input
                    type="number"
                    inputMode="decimal"
                    step="0.01"
                    min="0"
                    placeholder="0.00"
                    value={amount}
                    onChange={(e) => setAmount(e.target.value)}
                    className={cn(
                      'min-h-[52px] pl-8 font-semibold tabular-nums',
                      semanticTokens.radius.xl,
                      semanticTokens.typography.bodyLarge,
                      semanticTokens.motion.standard,
                      semanticTokens.focus.ring
                    )}
                    aria-label="Withdrawal amount"
                  />
                </div>
                <Text variant="caption" color="muted">
                  Available: ${availableBalance.toLocaleString('en-US', { minimumFractionDigits: 2, maximumFractionDigits: 2 })} {sourceStablecoin || 'Loading...'}
                </Text>
              </div>

              {/* Currency Selection */}
              <div className={cn("grid grid-cols-2", semanticTokens.gap.smMd)}>
                <div className={semanticTokens.stack.sm}>
                  <Label className={semanticTokens.typography.label}>Currency</Label>
                  <Select value={currency} onValueChange={(v) => setCurrency(v as 'USD' | 'EUR')}>
                    <SelectTrigger className={cn("min-h-[52px]", semanticTokens.radius.xl)}>
                      <SelectValue />
                    </SelectTrigger>
                    <SelectContent>
                      <SelectItem value="USD">
                        <div className={cn("flex items-center", semanticTokens.gap.sm)}>
                          <CurrencyIcon symbol="USD" size="sm" />
                          <span>USD</span>
                        </div>
                      </SelectItem>
                      <SelectItem value="EUR">
                        <div className={cn("flex items-center", semanticTokens.gap.sm)}>
                          <CurrencyIcon symbol="EUR" size="sm" />
                          <span>EUR</span>
                        </div>
                      </SelectItem>
                    </SelectContent>
                  </Select>
                </div>

                <div className={semanticTokens.stack.sm}>
                  <Label className={semanticTokens.typography.label}>Method</Label>
                  <Select value={paymentRail} onValueChange={(v) => setPaymentRail(v as 'ach' | 'wire' | 'sepa')}>
                    <SelectTrigger className={cn("min-h-[52px]", semanticTokens.radius.xl)}>
                      <SelectValue />
                    </SelectTrigger>
                    <SelectContent>
                      {currency === 'USD' ? (
                        <>
                          <SelectItem value="ach">ACH (1-3 days)</SelectItem>
                          <SelectItem value="wire">Wire (Same day)</SelectItem>
                        </>
                      ) : (
                        <>
                          <SelectItem value="sepa">SEPA (1 business day)</SelectItem>
                          <SelectItem value="wire">Wire (Same day)</SelectItem>
                        </>
                      )}
                    </SelectContent>
                  </Select>
                </div>
              </div>

              {/* Source Stablecoin Selector */}
              <div className={semanticTokens.stack.sm}>
                <Label className={semanticTokens.typography.label}>
                  Pay with
                </Label>
                {isLoadingWallets ? (
                  <Skeleton className={cn("h-[52px] w-full", semanticTokens.radius.xl)} />
                ) : (
                  <Select
                    value={sourceStablecoin}
                    onValueChange={setSourceStablecoin}
                    disabled={availableStablecoins.length === 0}
                  >
                  <SelectTrigger className={cn("min-h-[52px]", semanticTokens.radius.xl)}>
                    <SelectValue placeholder="Select stablecoin">
                      {sourceStablecoin ? (
                        <div className={cn("flex items-center justify-between w-full", semanticTokens.gap.md)}>
                          <div className={cn("flex items-center", semanticTokens.gap.sm)}>
                            <CurrencyIcon currency={sourceStablecoin} size="sm" />
                            <span>{sourceStablecoin}</span>
                          </div>
                          <span className="text-muted-foreground text-sm tabular-nums">
                            ${(availableStablecoins.find(s => s.currency === sourceStablecoin)?.balance || 0).toFixed(2)}
                          </span>
                        </div>
                      ) : null}
                    </SelectValue>
                  </SelectTrigger>
                  <SelectContent>
                    {availableStablecoins.length > 0 ? (
                      availableStablecoins.map(({ currency, balance }) => (
                        <SelectItem key={currency} value={currency}>
                          <div className="flex items-center justify-between w-full gap-4">
                            <div className={cn("flex items-center", semanticTokens.gap.sm)}>
                              <CurrencyIcon currency={currency} size="sm" />
                              <span>{currency}</span>
                            </div>
                            <span className="text-muted-foreground text-sm tabular-nums">
                              ${balance.toFixed(2)}
                            </span>
                          </div>
                        </SelectItem>
                      ))
                    ) : (
                      <SelectItem value="none" disabled>
                        No stablecoin balance
                      </SelectItem>
                    )}
                  </SelectContent>
                </Select>
                )}
              </div>

              {/* Inline Warning: No Balance */}
              {availableBalance === 0 && sourceStablecoin && (
                <Alert variant="warning">
                  <SFIcon icon={sfIcons.warning} className="h-4 w-4" />
                  <AlertTitle>No {sourceStablecoin} Balance</AlertTitle>
                  <AlertDescription>
                    You have no {sourceStablecoin} available.
                    {availableStablecoins.length > 1 ? (
                      <>
                        {' '}Try switching to {availableStablecoins.filter(s => s.currency !== sourceStablecoin)[0]?.currency}
                        {' '}(${availableStablecoins.filter(s => s.currency !== sourceStablecoin)[0]?.balance.toFixed(2)} available).
                      </>
                    ) : (
                      ' Add funds to your wallet first.'
                    )}
                  </AlertDescription>
                </Alert>
              )}

              {/* Cross-Currency Conversion Hint with Real-Time Rate */}
              {conversionInfo && (
                <Alert variant="info">
                  <SFIcon icon={sfIcons.info} className="h-4 w-4" />
                  <AlertTitle>Currency Conversion</AlertTitle>
                  <AlertDescription className="space-y-1">
                    {conversionInfo.isLoadingRate ? (
                      <p>Loading exchange rate...</p>
                    ) : (
                      <>
                        <p>
                          Your {sourceStablecoin} will be automatically converted to {currency}.
                        </p>
                        <div className="flex items-center gap-2 text-sm font-medium mt-2">
                          <span>Rate: 1 {sourceStablecoin} = {conversionInfo.rate.toFixed(4)} {currency}</span>
                          {conversionInfo.isRateStale && (
                            <span className="text-xs text-warning">⚠️ Updating...</span>
                          )}
                        </div>
                        {conversionInfo.sourceAmount > 0 && (
                          <p className="text-sm mt-1">
                            Estimated: <span className="font-semibold">${conversionInfo.sourceAmount.toFixed(2)} {sourceStablecoin}</span>
                            {' '}→{' '}
                            <span className="font-semibold">{currency === 'EUR' ? '€' : '$'}{conversionInfo.targetAmount.toFixed(2)} {currency}</span>
                          </p>
                        )}
                        {conversionInfo.rateUpdatedAt && (
                          <p className="text-xs text-muted-foreground mt-1">
                            Updated: {conversionInfo.rateUpdatedAt.toLocaleTimeString()}
                          </p>
                        )}
                      </>
                    )}
                  </AlertDescription>
                </Alert>
              )}

              {/* Review Screen - Fee Preview */}
              {showReview && (
                <div className={semanticTokens.stack.lg}>
                  <WithdrawSummaryCard
                    destinationType="bank"
                    amount={parseFloat(amount) || 0}
                    currency={currency}
                    fees={{
                      amount: parseFloat(amount) || 0,
                      totalFee: fees.totalFee,
                      youReceive: fees.youReceive,
                      feeDetails: fees.feeDetails,
                    }}
                    paymentRail={paymentRail}
                    bankName={selectedAccount.bank_name || 'Bank Account'}
                    accountLast4={selectedAccount.account_last4 || selectedAccount.account_number?.slice(-4) || '****'}
                    estimatedArrival={getEstimatedArrival(paymentRail, 'bank')}
                    sourceStablecoin={sourceStablecoin}
                    sourceAmount={conversionInfo?.sourceAmount}
                    exchangeRate={conversionInfo?.rate}
                  />

                  {/* High Fee Warning */}
                  {fees.isHighFee && (
                    <Alert variant="warning">
                      <ShieldAlert className="h-4 w-4" />
                      <AlertTitle>High Fee Warning</AlertTitle>
                      <AlertDescription>
                        Fees are {fees.feePercentage.toFixed(1)}% of your withdrawal amount.
                        Consider using a different payment method to reduce fees.
                      </AlertDescription>
                    </Alert>
                  )}
                </div>
              )}
            </div>
          )}

          {/* Step: Crypto - Enter Details */}
          {step === 'crypto' && (
            <div className={semanticTokens.stack.lg}>
              {/* Security Warning */}
              <Alert variant="warning">
                <ShieldAlert className={cn("h-4 w-4", semanticTokens.warning.accent)} />
                <AlertTitle>External Wallet Transfer</AlertTitle>
                <AlertDescription className="text-sm">
                  <ul className="list-disc list-inside space-y-1 mt-2">
                    <li>You are sending to an <strong>external wallet</strong> outside Supabank</li>
                    <li>Transactions are <strong>irreversible</strong> once confirmed</li>
                    <li>Double-check the address and network match</li>
                    <li><strong>You are fully responsible</strong> for the accuracy of the destination</li>
                  </ul>
                </AlertDescription>
              </Alert>

              {/* Source Wallet Selection */}
              <div className={semanticTokens.stack.sm}>
                <Label className={semanticTokens.typography.label}>1. Select Network</Label>
                {isLoadingWallets ? (
                  <Skeleton className={cn("h-[52px] w-full", semanticTokens.radius.xl)} />
                ) : wallets && wallets.length > 0 ? (
                  <Select
                    value={selectedWallet?.id || ''}
                    onValueChange={(id) => {
                      const wallet = wallets.find(w => w.id === id);
                      setSelectedWallet(wallet || null);
                      setCryptoCurrency(''); // Reset token selection when wallet changes
                      // Auto-set destination chain to match source wallet
                      if (wallet?.chain) {
                        setDestinationChain(wallet.chain.toLowerCase());
                        // Clear saved address if chain doesn't match
                        if (selectedSavedAddress && selectedSavedAddress.chain !== wallet.chain.toLowerCase()) {
                          setSelectedSavedAddress(null);
                          setDestinationAddress('');
                        }
                      }
                    }}
                  >
                    <SelectTrigger className={cn("min-h-[52px]", semanticTokens.radius.xl)}>
                      <SelectValue placeholder="Select network">
                        {selectedWallet ? (
                          <div className={cn("flex items-center", semanticTokens.gap.sm)}>
                            <NetworkIcon network={selectedWallet.chain || 'ethereum'} className="h-5 w-5" />
                            <span>{selectedWallet.chain?.charAt(0).toUpperCase() + selectedWallet.chain?.slice(1)}</span>
                          </div>
                        ) : null}
                      </SelectValue>
                    </SelectTrigger>
                    <SelectContent
                      className="z-[10000]"
                      position="popper"
                      sideOffset={4}
                    >
                      {wallets.map((wallet) => {
                        const chainName = wallet.chain?.charAt(0).toUpperCase() + wallet.chain?.slice(1) || 'Unknown';
                        return (
                          <SelectItem key={wallet.id} value={wallet.id}>
                            <div className={cn("flex items-center", semanticTokens.gap.sm)}>
                              <NetworkIcon network={wallet.chain || 'ethereum'} className="h-5 w-5" />
                              <span>{chainName}</span>
                            </div>
                          </SelectItem>
                        );
                      })}
                    </SelectContent>
                  </Select>
                ) : (
                  <div className={cn(
                    semanticTokens.spacing.md,
                    semanticTokens.radius.xl,
                    "text-center",
                    semanticTokens.bg.muted
                  )}>
                    <Text variant="sm" color="muted">No wallets available</Text>
                  </div>
                )}
              </div>

              {/* Token Selection - Only show after wallet is selected */}
              {selectedWallet && selectedWallet.balances.length > 0 && (
                <div className={semanticTokens.stack.sm}>
                  <Label className={semanticTokens.typography.label}>2. Select Token</Label>
                  <Select
                    value={cryptoCurrency}
                    onValueChange={(value) => setCryptoCurrency(value)}
                  >
                    <SelectTrigger className={cn("min-h-[52px]", semanticTokens.radius.xl)}>
                      <SelectValue placeholder="Select token to withdraw">
                        {cryptoCurrency ? (
                          <div className={cn("flex items-center", semanticTokens.gap.sm)}>
                            <CurrencyIcon currency={cryptoCurrency} size="sm" />
                            <span>{cryptoCurrency.toUpperCase()}</span>
                            <span className="text-muted-foreground text-xs">
                              (Balance: {selectedWallet.balances.find(b => b.currency.toUpperCase() === cryptoCurrency.toUpperCase())?.balance || '0'})
                            </span>
                          </div>
                        ) : null}
                      </SelectValue>
                    </SelectTrigger>
                    <SelectContent
                      className="z-[10000]"
                      position="popper"
                      sideOffset={4}
                    >
                      {selectedWallet.balances.map((balance) => {
                        const hasBalance = parseFloat(balance.balance) > 0;
                        return (
                          <SelectItem
                            key={balance.currency}
                            value={balance.currency.toLowerCase()}
                            disabled={!hasBalance}
                          >
                            <div className={cn("flex items-center justify-between w-full", semanticTokens.gap.md)}>
                              <div className={cn("flex items-center", semanticTokens.gap.sm)}>
                                <CurrencyIcon currency={balance.currency} size="sm" />
                                <span>{balance.currency.toUpperCase()}</span>
                              </div>
                              <span className={cn(
                                "tabular-nums text-sm",
                                hasBalance ? "text-foreground" : "text-muted-foreground"
                              )}>
                                {parseFloat(balance.balance).toFixed(4)}
                              </span>
                            </div>
                          </SelectItem>
                        );
                      })}
                    </SelectContent>
                  </Select>
                </div>
              )}

              {/* Amount Input - Only show after token is selected */}
              {cryptoCurrency && (
                <div className={semanticTokens.stack.sm}>
                  <Label className={semanticTokens.typography.label}>3. Amount ({cryptoCurrency.toUpperCase()})</Label>
                  <div className="relative">
                    <Input
                      type="number"
                      inputMode="decimal"
                      step="0.01"
                      min="0"
                      placeholder="0.00"
                      value={amount}
                      onChange={(e) => setAmount(e.target.value)}
                      className={cn(
                        'min-h-[52px] font-semibold tabular-nums',
                        semanticTokens.radius.xl,
                        semanticTokens.typography.bodyLarge,
                        semanticTokens.motion.standard,
                        semanticTokens.focus.ring
                      )}
                      aria-label="Transfer amount"
                    />
                  </div>
                  {selectedWallet && (
                    <Text variant="caption" color="muted">
                      Available: {selectedWallet.balances.find(b => b.currency.toUpperCase() === cryptoCurrency.toUpperCase())?.balance || '0'} {cryptoCurrency.toUpperCase()}
                    </Text>
                  )}
                </div>
              )}

              {/* Destination: Only show after wallet AND token are selected */}
              {selectedWallet && cryptoCurrency && (
                <div className="space-y-3">
                  <Label className={semanticTokens.typography.label}>4. Destination Address</Label>

                  {/* Saved Addresses (filtered by selected wallet's chain) */}
                  {isLoadingSavedAddresses ? (
                    <Skeleton className={cn("h-[60px] w-full", semanticTokens.radius.xl)} />
                  ) : savedAddresses && savedAddresses.filter(a => a.chain === selectedWallet.chain?.toLowerCase()).length > 0 ? (
                    <div className={semanticTokens.stack.sm}>
                      <Text variant="caption" color="muted" className="flex items-center gap-1">
                        <SFIcon icon={sfIcons.wallet} className="h-3 w-3" /> Saved wallets on {(selectedWallet?.chain || 'ethereum').charAt(0).toUpperCase() + (selectedWallet?.chain || 'ethereum').slice(1)}
                      </Text>
                      <div className={cn(semanticTokens.stack.sm, "max-h-[200px] overflow-y-auto")}>
                        {savedAddresses
                          .filter(a => a.chain === selectedWallet?.chain?.toLowerCase())
                          .map((addr) => (
                            <div
                              key={addr.id}
                              onClick={() => {
                                setSelectedSavedAddress(addr);
                                setDestinationAddress(addr.address);
                              }}
                              className={cn(
                                'flex items-center cursor-pointer border',
                                semanticTokens.gap.smMd,
                                semanticTokens.spacing.smMd,
                                semanticTokens.radius.xl,
                                semanticTokens.motion.standard,
                                selectedSavedAddress?.id === addr.id
                                  ? 'border-primary bg-primary/5'
                                  : 'border-border/50 hover:border-border bg-card/50 hover:bg-card/80'
                              )}
                              role="button"
                              tabIndex={0}
                              onKeyDown={(e) => e.key === 'Enter' && (setSelectedSavedAddress(addr), setDestinationAddress(addr.address))}
                            >
                              <div className="relative flex-shrink-0">
                                <NetworkIcon network={addr.chain} className="h-8 w-8" />
                                <div className="absolute -bottom-0.5 -right-0.5 bg-background rounded-full p-0.5">
                                  <CurrencyIcon currency={addr.currency || 'usdc'} size="xs" />
                                </div>
                              </div>
                              <div className="flex-1 min-w-0">
                                <div className={cn("flex items-center", semanticTokens.gap.sm)}>
                                  <Text variant="sm" className="font-medium truncate">{addr.label}</Text>
                                  <span className={cn(
                                    'text-xs px-1.5 py-0.5 rounded font-medium',
                                    'bg-primary/10 text-primary'
                                  )}>
                                    {(addr.currency || 'usdc').toUpperCase()}
                                  </span>
                                </div>
                                <code className={cn(semanticTokens.typography.caption, semanticTokens.fg.muted, 'font-mono')}>
                                  {addr.address.slice(0, 8)}...{addr.address.slice(-6)}
                                </code>
                              </div>
                              {selectedSavedAddress?.id === addr.id && (
                                <SFIcon icon={sfIcons.success} className="h-5 w-5 text-primary flex-shrink-0" />
                              )}
                            </div>
                          ))
                        }
                      </div>
                      <div className={cn("flex items-center pt-2", semanticTokens.gap.sm)}>
                        <div className="flex-1 h-px bg-border" />
                        <Text variant="caption" color="muted">or enter manually</Text>
                        <div className="flex-1 h-px bg-border" />
                      </div>
                    </div>
                  ) : null}

                  {/* Manual Address Entry */}
                  <Input
                    placeholder={selectedSavedAddress ? selectedSavedAddress.address : "Enter wallet address"}
                    value={destinationAddress}
                    onChange={(e) => {
                      setDestinationAddress(e.target.value);
                      // Clear selected saved address if user types manually
                      if (selectedSavedAddress && e.target.value !== selectedSavedAddress.address) {
                        setSelectedSavedAddress(null);
                      }
                    }}
                    className={cn(
                      'min-h-[52px]',
                      semanticTokens.radius.xl,
                      semanticTokens.typography.code,
                      semanticTokens.motion.standard,
                      semanticTokens.focus.ring
                    )}
                    aria-label="Destination wallet address"
                  />
                  <Text variant="caption" color="muted">
                    Make sure the address supports {(selectedWallet?.chain || 'the selected').charAt(0).toUpperCase() + (selectedWallet?.chain || 'the selected').slice(1)} network
                  </Text>
                </div>
              )}

              {/* Network Badge (only show when wallet is selected) */}
              {selectedWallet && (
                <div className={cn(
                  'flex items-center',
                  semanticTokens.gap.smMd,
                  semanticTokens.spacing.smMd,
                  semanticTokens.radius.xl,
                  semanticTokens.bg.muted
                )}>
                  <NetworkIcon network={selectedWallet.chain || destinationChain} className="h-5 w-5" />
                  <Text variant="sm" color="muted">
                    Sending on <span className="font-medium text-foreground">{(selectedWallet.chain || destinationChain).charAt(0).toUpperCase() + (selectedWallet.chain || destinationChain).slice(1)}</span> network
                  </Text>
                </div>
              )}
            </div>
          )}

        </div>

        {/* Footer */}
        <SheetFooter className={cn(
          'flex-shrink-0 border-t pt-4',
          isMobile && 'pb-6'
        )}>
          {step === 'choose' ? (
            <Button
              variant="outline"
              className={cn('w-full min-h-[52px]', semanticTokens.radius.xl)}
              onClick={onClose}
            >
              Cancel
            </Button>
          ) : step === 'bank' && selectedAccount && !showReview ? (
            <Button
              className={cn('w-full min-h-[52px]', semanticTokens.radius.xl)}
              onClick={handleProceedToReview}
              disabled={!canProceed || isPending}
            >
              {!canProceed && amount && parseFloat(amount) > 0 ? (
                <>
                  <SFIcon icon={sfIcons.warning} className="mr-2 h-5 w-5" />
                  Insufficient Balance
                </>
              ) : (
                <>
                  <ArrowUpRight className="mr-2 h-5 w-5" />
                  Review Withdrawal
                </>
              )}
            </Button>
          ) : step === 'bank' && selectedAccount && showReview ? (
            <div className={cn("flex", semanticTokens.gap.smMd, 'w-full')}>
              <Button
                variant="outline"
                className={cn('flex-1 min-h-[52px]', semanticTokens.radius.xl)}
                onClick={() => setShowReview(false)}
                disabled={isPending}
              >
                Back to Edit
              </Button>
              <Button
                className={cn('flex-1 min-h-[52px]', semanticTokens.radius.xl)}
                onClick={handleConfirmWithdraw}
                disabled={isPending}
              >
                {isPending ? (
                  <>
                    <Loader2 className="mr-2 h-5 w-5 animate-spin" />
                    Processing...
                  </>
                ) : (
                  <>
                    <Zap className="mr-2 h-5 w-5" />
                    Confirm {currency === 'EUR' ? '€' : '$'}{(parseFloat(amount) || 0).toFixed(2)}
                  </>
                )}
              </Button>
            </div>
          ) : step === 'crypto' ? (
            <Button
              className={cn('w-full min-h-[52px]', semanticTokens.radius.xl)}
              onClick={handleCryptoWithdraw}
              disabled={!isValid || isPending}
            >
              {isPending ? (
                <>
                  <Loader2 className="mr-2 h-5 w-5 animate-spin" />
                  Processing...
                </>
              ) : (
                <>
                  <ArrowUpRight className="mr-2 h-5 w-5" />
                  Send {amount && parseFloat(amount) > 0 ? `${parseFloat(amount).toFixed(2)} USDC` : ''}
                </>
              )}
            </Button>
          ) : (
            <Button
              variant="outline"
              className={cn('w-full min-h-[52px]', semanticTokens.radius.xl)}
              onClick={onClose}
            >
              Done
            </Button>
          )}
        </SheetFooter>
      </SheetContent>
    </Sheet>
  );
}

